import { useCallback, useEffect } from 'react';

import { useSegments } from 'expo-router';
import type { PromotionExposureCandidate } from 'features/promotions/types';
import { AppState } from 'react-native';

import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import { isNative } from 'utils/environment';

export type PromotionExposureScope = 'home' | 'rewards-layout' | 'cart';

export const isPersonalizedPromotionExposureRoute = (
  scope: PromotionExposureScope,
  segments: readonly string[]
): boolean => {
  const [routeGroup, route] = segments;

  switch (scope) {
    case 'home':
      return routeGroup === '(tabs)' && route === 'index';
    case 'rewards-layout':
      return routeGroup === '(tabs)' && route === 'rewards';
    case 'cart':
      return routeGroup === '(cart)' && route === 'cart';
  }
};

const getIsAppVisible = (): boolean => {
  if (isNative) {
    return (AppState.currentState ?? 'active') === 'active';
  }

  return typeof document === 'undefined' || document.visibilityState === 'visible';
};

interface UseLogPersonalizedPromotionExposureArgs {
  candidate?: PromotionExposureCandidate;
  exposureScope: PromotionExposureScope;
  hasRenderablePromotion: boolean;
}

export const useLogPersonalizedPromotionExposure = ({
  candidate,
  exposureScope,
  hasRenderablePromotion,
}: UseLogPersonalizedPromotionExposureArgs): void => {
  const segments = useSegments() as readonly string[];
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  const isCurrentScope = isPersonalizedPromotionExposureRoute(exposureScope, segments);
  const shouldLog =
    candidate?.kind === 'empty' || (candidate?.kind === 'promotion' && hasRenderablePromotion);
  const logExposure = candidate?.logExposure;

  const attemptExposure = useCallback(() => {
    if (!shouldLog || !isCurrentScope || !getIsAppVisible()) {
      return;
    }

    logExposure?.();
  }, [isCurrentScope, logExposure, shouldLog]);

  useEffect(() => {
    attemptExposure();
    addAppStateChangeListeners(attemptExposure);

    return removeAppStateChangeListeners;
  }, [addAppStateChangeListeners, attemptExposure, removeAppStateChangeListeners]);
};
